import { type RefCallback, useCallback, useEffect, useRef } from 'react'
import { type InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import {
  type Observable,
  animationFrameScheduler,
  distinctUntilChanged,
  combineLatestWith,
  BehaviorSubject,
  observeOn,
  filter,
  EMPTY,
  of,
  delay,
  switchMap,
} from 'rxjs'
import { useObservable } from '@/hooks/rx-hooks'
import { tailMap, withAbortSignal } from '@/utils/rx-util'
import { toStream } from '@/utils/mobx-util'
import { shallowItemsAreEqual } from '@/utils/array-util'

export interface UseInfiniteScrollProps {
  /**
   * The infinite scroll provider.
   * If not specified, no viewport handler is registered.
   */
  scroller?: InfiniteScrollProvider<unknown>

  /**
   * Root element (viewport) used for checking intersection.
   * Defaults to the window/document.
   */
  root?: Element

  /**
   * If specified, this amount will delay the change of the 'active' state
   * on the scroller when it transitions to `true`. This can be useful
   * when UI updates for new elements are slower, which would result in
   * the tracked element still being considered visible, which would trigger
   * another fetch.
   */
  activeDelay?: number

  /**
   * One or more margins to apply to the root element or window viewport.
   * This can extend or contract the point at which the element is
   * considered inside the viewport.
   */
  margin?: string
}

export interface UseInfiniteScrollResult {
  /**
   * Ref connector for the element to monitor.
   * @param element
   */
  connectRef: RefCallback<Element>
}

/**
 * Triggers infinite scroll when a target element is in view.
 */
export function useInfiniteScroll({
  scroller,
  root,
  margin,
  activeDelay = 0,
}: UseInfiniteScrollProps): UseInfiniteScrollResult {
  const observerRef = useRef<InfiniteScrollIntersectionObserver | null>(null)
  observerRef.current ??= new InfiniteScrollIntersectionObserver()

  const inView$ = observerRef.current.inView$

  useEffect(() => {
    observerRef.current?.receiveProps(root, margin)
  }, [root, margin])

  useObservable(
    useCallback(
      () =>
        scroller ? infiniteScrollStream(scroller, inView$, activeDelay) : EMPTY,
      [scroller, inView$, activeDelay],
    ),
  )

  const connect = useCallback((element: Element) => {
    observerRef.current?.add(element)
    return () => {
      observerRef.current?.remove(element)
    }
  }, [])

  return { connectRef: connect }
}

/**
 * Queries for more items using the scroller while the trigger zone is
 * still in view.
 *
 * @param scroller
 * @param inView$
 * @param activeDelay
 */
function infiniteScrollStream(
  scroller: InfiniteScrollProvider<unknown>,
  inView$: Observable<boolean>,
  activeDelay: number,
) {
  // `active` means that the provider is accepting `more` requests - that is,
  // it's not currently loading items, and it's not paged to the end.
  const active$ = toStream(() => scroller.active, true).pipe(
    // For changes to `active`, wait until the browser has performed
    // layout updates from other potential changes to the scroll provider
    // in case the infinite scroll zone is leaving the viewport in the
    // next paint.
    //
    // To do this, by default we defer observing the value by 2 frames if
    // the caller didn't explicitly specify a delay to use.
    // That should give the `inView$` time to emit a new value, if any,
    // which could mean we would want to skip further calls.
    switchMap((isActive) => {
      if (!isActive) {
        // Emit `false` immediately to stop sending more requests.
        return of(false)
      }

      if (activeDelay === 0) {
        return of(true).pipe(
          observeOn(animationFrameScheduler),
          observeOn(animationFrameScheduler),
        )
      }

      // Add a delay when becoming active.
      return of(true).pipe(delay(activeDelay))
    }),
  )

  return inView$.pipe(
    combineLatestWith(active$),
    distinctUntilChanged(shallowItemsAreEqual),
    // Only trigger queries while in view and while active.
    filter(([inView, active]) => inView && active),
    // Only do a single request at a time; if another comes in, it'll happen
    // after.
    tailMap(() => withAbortSignal((signal) => scroller.more(signal))),
  )
}

/**
 * Manages tracked elements for infinite scroll trigger detection.
 */
class InfiniteScrollIntersectionObserver {
  private readonly _inViewSubject = new BehaviorSubject(false)
  private readonly _elements = new Map<Element, TrackedElement>()

  private _observer: IntersectionObserver | null = null

  /**
   * Emits when any element is in view.
   */
  readonly inView$ = this._inViewSubject.asObservable()

  /**
   * Updates the observer properties by re-creating the observer with the new
   * options.
   *
   * @param root
   * @param rootMargin
   */
  receiveProps(root: Element | undefined, rootMargin: string | undefined) {
    if (!this._observer) {
      this._createObserver(root, rootMargin)
      return
    }

    if (
      this._observer.root === root &&
      this._observer.rootMargin === rootMargin
    ) {
      return
    }

    this._createObserver(root, rootMargin)
  }

  /**
   * Adds an element to the tracking map and starts observing it.
   */
  add(element: Element) {
    if (this._elements.has(element)) {
      return
    }

    this._elements.set(element, { element, inView: false })
    this._observer?.observe(element)
    this._checkInView()
  }

  /**
   * Removes the element.
   */
  remove(element: Element) {
    if (!this._elements.has(element)) {
      return
    }

    this._observer?.unobserve(element)
    this._elements.delete(element)
    this._checkInView()
  }

  /**
   * Creates a new observer instance.
   *
   * @param root
   * @param rootMargin
   * @private
   */
  private _createObserver(
    root: Element | undefined,
    rootMargin: string | undefined,
  ) {
    if (this._observer) {
      this._observer.disconnect()
    }

    this._observer = new IntersectionObserver(this._observerCallback, {
      root,
      rootMargin,
    })

    for (const element of this._elements.keys()) {
      this._observer.observe(element)
    }
  }

  /**
   * The callback for the intersection observer.
   */
  private _observerCallback: IntersectionObserverCallback = (entries) => {
    for (const entry of entries) {
      const trackedElement = this._elements.get(entry.target)
      if (!trackedElement) {
        continue
      }

      trackedElement.inView = entry.isIntersecting
    }

    this._checkInView()
  }

  /**
   * Checks whether any of the tracked elements are in view.
   */
  private _checkInView() {
    let inView = false
    for (const trackedElement of this._elements.values()) {
      if (trackedElement.inView) {
        inView = true
        break
      }
    }

    if (this._inViewSubject.value !== inView) {
      this._inViewSubject.next(inView)
    }
  }
}

/**
 * A tracked element.
 */
interface TrackedElement {
  /**
   * The element.
   */
  readonly element: Element

  /**
   * Whether the element is currently in view.
   */
  inView: boolean
}
